// ApiWebhooksSettings — Settings → "API & Webhooks" (docs/api-webhooks-spec.md §6; data model §2, management
// actions §4, inbound API §5). Owner/admin only (the rail hides it; RLS + the manage functions enforce it).
//
// Sections, top to bottom: API keys · Webhooks · Recent deliveries · Recent API calls · Docs.
// Reads:  webhook_endpoints / webhook_deliveries / org_events / api_request_log straight through PostgREST (RLS
//         scopes them to this org; `secret` is column-revoked so every select names its columns — never `*`).
//         Keys come from `manage-api-keys {action:"list"}` (org_api_keys is service-role-only except the safe
//         column grant in sql/089, and the list action is the one source of truth).
// Writes: ALL through `manage-webhooks` / `manage-api-keys` (sb.fn) — the browser never inserts a secret or hash.
// Plaintext keys / signing secrets are shown exactly once in a modal that cannot be dismissed by accident.
import { useCallback, useEffect, useMemo, useState } from "react";
import { sb, SUPABASE_URL, SUPABASE_ANON_KEY, getAuthToken } from "../lib/supabase";
import { useIsMobile } from "../lib/useIsMobile";
import {
  API_ENDPOINTS, API_ERRORS, API_VERSION, EVENT_CATALOG, GHL_CONNECT_STEPS, GHL_NOTE, GHL_REVERSE_STEPS,
  NODE_VERIFY_SNIPPET, PYTHON_VERIFY_SNIPPET, SCOPES, SIGNATURE_HEADERS, SUBSCRIBABLE_EVENTS,
  apiBaseUrl, curlExample, exampleEnvelope, isPublicHttpsUrl,
} from "../lib/apiDocs";

const H = () => ({ apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${getAuthToken()}` });
// Explicit column lists: webhook_endpoints.secret is REVOKED from authenticated, so `select=*` would 401 the
// whole row. Keep these in step with sql/089.
const ENDPOINT_COLS = "id,url,description,events,active,created_at,updated_at,last_success_at,last_failure_at,consecutive_failures,disabled_reason";
const DELIVERY_COLS = "id,endpoint_id,event_id,event,status,attempts,next_attempt_at,last_status_code,last_error,response_excerpt,created_at,delivered_at";
const LOG_COLS      = "id,api_key_id,method,path,status_code,ip,duration_ms,created_at";
const EVENT_COLS    = "id,event,dedupe_key,payload,created_at";

async function rest(pathAndQuery) {
  try {
    const r = await fetch(`${SUPABASE_URL}/rest/v1/${pathAndQuery}`, { headers: H() });
    if (!r.ok) return [];
    const d = await r.json();
    return Array.isArray(d) ? d : [];
  } catch { return []; }
}

// ── tiny presentational helpers ───────────────────────────────────────────────────────────────────
const AMBER = { background: "#FEF6E7", color: "#8A5A06", border: "1px solid #F6D88A" };
const MONO  = "ui-monospace,SFMono-Regular,Menlo,monospace";
function Pill({ tone = "gray", children, title }) {
  if (tone === "amber") return <span className="badge" style={AMBER} title={title}>{children}</span>;
  return <span className={`badge badge-${tone}`} title={title}>{children}</span>;
}
function ago(ts) {
  if (!ts) return "—";
  const s = Math.max(0, (Date.now() - new Date(ts).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 86400 * 14) return `${Math.floor(s / 86400)}d ago`;
  return new Date(ts).toLocaleDateString();
}
function whenTitle(ts) { return ts ? new Date(ts).toLocaleString() : ""; }
function hostOf(url) { try { return new URL(url).host; } catch { return url; } }
function pathOf(url) { try { const u = new URL(url); return u.pathname + u.search; } catch { return ""; } }
const DELIVERY_TONE = { delivered: "green", pending: "blue", failed: "amber", dead: "red" };
const codeTone = (c) => (!c ? "gray" : c < 300 ? "green" : c < 500 ? "amber" : "red");
const eventLabel = (ev) => (EVENT_CATALOG.find((e) => e.event === ev) || {}).label || ev;
const pre = { margin: 0, padding: "12px 14px", background: "var(--text)", color: "var(--surface)", borderRadius: "var(--r-sm, 8px)", fontSize: 12, lineHeight: 1.5, fontFamily: MONO, overflowX: "auto", whiteSpace: "pre" };
const code = { fontFamily: MONO, fontSize: 12, background: "rgba(0,0,0,.05)", padding: "1px 5px", borderRadius: 4 };
const subText = { fontSize: 12.5, color: "var(--text2)", lineHeight: 1.5 };

function EventPills({ events }) {
  const list = events || [];
  const shown = list.slice(0, 3);
  return (
    <span style={{ display: "inline-flex", gap: 4, flexWrap: "wrap" }}>
      {shown.map((ev) => <Pill key={ev} tone="blue" title={ev}>{eventLabel(ev)}</Pill>)}
      {list.length > 3 && <Pill tone="gray" title={list.slice(3).join(", ")}>+{list.length - 3}</Pill>}
      {list.length === 0 && <Pill tone="gray">no events</Pill>}
    </span>
  );
}

// Copy-to-clipboard with the app's idiom (writeText + 3 s "Copied" + try/catch for non-secure contexts).
function CopyButton({ text, label = "Copy", className = "btn btn-ghost btn-sm" }) {
  const [state, setState] = useState("");
  async function copy() {
    try { await navigator.clipboard.writeText(text); setState("ok"); setTimeout(() => setState(""), 3000); }
    catch (e) { console.warn("clipboard copy failed", e); setState("err"); }
  }
  return <button type="button" className={className} onClick={copy}>{state === "ok" ? "✓ Copied" : state === "err" ? "Select & copy" : label}</button>;
}

// The one-time reveal. No overlay-click dismiss — the operator must press the button, so a stray click
// can't lose a secret that will never be shown again.
function SecretModal({ title, intro, value, onClose }) {
  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 520 }}>
        <div className="modal-header"><h3>{title}</h3></div>
        <div className="modal-body">
          <div className="alert alert-error" style={{ marginBottom: 14 }}>
            <strong>Shown once.</strong> Copy this now and store it somewhere safe — we only keep a hash, so it can't be shown again. If you lose it, rotate or create a new one.
          </div>
          {intro && <p style={{ ...subText, margin: "0 0 10px" }}>{intro}</p>}
          <input readOnly value={value || ""} onFocus={(e) => e.target.select()} aria-label={title}
                 style={{ width: "100%", boxSizing: "border-box", fontFamily: MONO, fontSize: 12.5 }} />
          <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
            <CopyButton text={value || ""} label="Copy to clipboard" className="btn btn-gold" />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>I've saved it</button>
        </div>
      </div>
    </div>
  );
}

function EventChecklist({ value, onChange }) {
  const groups = useMemo(() => {
    const g = {};
    EVENT_CATALOG.filter((e) => !e.system).forEach((e) => { (g[e.group] = g[e.group] || []).push(e); });
    return g;
  }, []);
  const toggle = (ev) => onChange(value.includes(ev) ? value.filter((x) => x !== ev) : [...value, ev]);
  const all = value.length === SUBSCRIBABLE_EVENTS.length;
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
        <label>Events</label>
        <button type="button" className="btn btn-ghost btn-xs" onClick={() => onChange(all ? [] : [...SUBSCRIBABLE_EVENTS])}>{all ? "Clear all" : "Select all"}</button>
      </div>
      {Object.entries(groups).map(([group, evs]) => (
        <div key={group} style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: ".06em", margin: "6px 0 4px" }}>{group}</div>
          {evs.map((e) => (
            <label key={e.event} style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "6px 0", cursor: "pointer", fontSize: 13, color: "var(--text)", fontWeight: 400 }}>
              <input type="checkbox" checked={value.includes(e.event)} onChange={() => toggle(e.event)} style={{ marginTop: 3 }} />
              <span style={{ minWidth: 0 }}>
                <span style={{ fontWeight: 600 }}>{e.label}</span> <span style={code}>{e.event}</span>
                <div style={subText}>{e.desc}</div>
              </span>
            </label>
          ))}
        </div>
      ))}
    </div>
  );
}

// Add / edit an endpoint. `initial` set = edit mode (url/description/events only; the secret is untouched).
function EndpointModal({ initial, busy, onSave, onClose }) {
  const [url, setUrl] = useState(initial?.url || "");
  const [description, setDescription] = useState(initial?.description || "");
  const [events, setEvents] = useState(initial?.events || ["job.on_the_way", "job.completed", "job.skipped"]);
  const [err, setErr] = useState("");
  function submit() {
    if (!isPublicHttpsUrl(url)) { setErr("Enter a public https:// URL (no http, localhost or private addresses)."); return; }
    if (events.length === 0) { setErr("Pick at least one event."); return; }
    setErr("");
    onSave({ url: url.trim(), description: description.trim() || null, events });
  }
  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && !busy && onClose()}>
      <div className="modal" style={{ maxWidth: 560 }}>
        <div className="modal-header">
          <h3>{initial ? "Edit endpoint" : "Add endpoint"}</h3>
          <button className="close-btn" onClick={onClose} disabled={busy}>×</button>
        </div>
        <div className="modal-body" style={{ display: "grid", gap: 14 }}>
          <div className="form-group">
            <label>Endpoint URL</label>
            <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://services.leadconnectorhq.com/hooks/…" autoComplete="off" spellCheck={false} />
            <div style={subText}>Must be https and publicly reachable. Paste GoHighLevel's Inbound Webhook URL here.</div>
          </div>
          <div className="form-group">
            <label>Description (optional)</label>
            <input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="GHL — on-the-way SMS workflow" maxLength={120} />
          </div>
          <EventChecklist value={events} onChange={setEvents} />
          {err && <div className="alert alert-error" style={{ margin: 0 }}>{err}</div>}
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn btn-gold" onClick={submit} disabled={busy}>{busy ? "Saving…" : initial ? "Save changes" : "Add endpoint"}</button>
        </div>
      </div>
    </div>
  );
}

function CreateKeyModal({ busy, onSave, onClose }) {
  const [label, setLabel] = useState("");
  const [scopes, setScopes] = useState(["read"]);
  const [rate, setRate] = useState(60);
  const [err, setErr] = useState("");
  const toggle = (k) => setScopes((s) => (s.includes(k) ? s.filter((x) => x !== k) : [...s, k]));
  function submit() {
    if (!label.trim()) { setErr("Give the key a label so you know where it's used."); return; }
    if (scopes.length === 0) { setErr("Pick at least one scope."); return; }
    const r = Number(rate);
    if (!Number.isInteger(r) || r < 1 || r > 600) { setErr("Rate limit must be 1–600 requests per minute."); return; }
    setErr("");
    onSave({ label: label.trim(), scopes, rate_per_min: r });
  }
  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && !busy && onClose()}>
      <div className="modal" style={{ maxWidth: 480 }}>
        <div className="modal-header">
          <h3>Create API key</h3>
          <button className="close-btn" onClick={onClose} disabled={busy}>×</button>
        </div>
        <div className="modal-body" style={{ display: "grid", gap: 14 }}>
          <div className="form-group">
            <label>Label</label>
            <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="GoHighLevel workflows" maxLength={80} autoFocus />
          </div>
          <div>
            <label>Scopes</label>
            {SCOPES.map((s) => (
              <label key={s.key} style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "6px 0", cursor: "pointer", fontSize: 13, color: "var(--text)", fontWeight: 400 }}>
                <input type="checkbox" checked={scopes.includes(s.key)} onChange={() => toggle(s.key)} style={{ marginTop: 3 }} />
                <span><span style={{ fontWeight: 600 }}>{s.label}</span><div style={subText}>{s.desc}</div></span>
              </label>
            ))}
          </div>
          <div className="form-group">
            <label>Rate limit (requests per minute)</label>
            <input type="number" min={1} max={600} value={rate} onChange={(e) => setRate(e.target.value)} style={{ width: 140 }} />
          </div>
          {err && <div className="alert alert-error" style={{ margin: 0 }}>{err}</div>}
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn btn-gold" onClick={submit} disabled={busy}>{busy ? "Creating…" : "Create key"}</button>
        </div>
      </div>
    </div>
  );
}

// Right-hand drawer (Leads idiom: overlay + modal pinned right on desktop, bottom sheet on phones).
function DeliveryDrawer({ delivery, endpoint, payload, loading, onRedeliver, busy, onClose }) {
  const isMobile = useIsMobile();
  const sheet = isMobile
    ? { width: "100%", maxWidth: "100%", maxHeight: "85dvh", borderRadius: "var(--r-lg) var(--r-lg) 0 0", display: "flex", flexDirection: "column", overflow: "hidden" }
    : { width: 520, maxWidth: "94vw", height: "100%", maxHeight: "100%", borderRadius: 0, display: "flex", flexDirection: "column" };
  const d = delivery;
  const row = (k, v) => (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 12, padding: "7px 0", borderBottom: "1px solid var(--border)", fontSize: 13 }}>
      <span style={{ color: "var(--text2)", flexShrink: 0 }}>{k}</span>
      <span style={{ textAlign: "right", minWidth: 0, overflowWrap: "anywhere" }}>{v}</span>
    </div>
  );
  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()} style={{ justifyContent: isMobile ? "center" : "flex-end", alignItems: isMobile ? "flex-end" : "stretch", padding: 0 }}>
      <div className="modal" style={sheet}>
        <div className="modal-header">
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 600, letterSpacing: "-.01em" }}>{eventLabel(d.event)} <span style={code}>{d.event}</span></div>
            <div style={subText}>{endpoint ? hostOf(endpoint.url) : "endpoint removed"} · {ago(d.created_at)}</div>
          </div>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        <div className="modal-body" style={{ flex: 1, overflowY: "auto" }}>
          <div style={{ marginBottom: 16 }}>
            {row("Status", <Pill tone={DELIVERY_TONE[d.status] || "gray"}>{d.status}</Pill>)}
            {row("Attempts", d.attempts)}
            {row("Last response", d.last_status_code ? <Pill tone={codeTone(d.last_status_code)}>{d.last_status_code}</Pill> : "—")}
            {d.delivered_at && row("Delivered", whenTitle(d.delivered_at))}
            {(d.status === "pending" || d.status === "failed") && row("Next attempt", whenTitle(d.next_attempt_at))}
            {d.last_error && row("Last error", <span style={{ color: "var(--danger)" }}>{d.last_error}</span>)}
            {d.response_excerpt && row("Response", <span style={{ fontFamily: MONO, fontSize: 12 }}>{d.response_excerpt}</span>)}
            {row("Delivery id", <span style={code}>{d.id}</span>)}
          </div>
          <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 6 }}>Payload sent</div>
          {loading ? <div style={subText}>Loading…</div>
            : payload ? <pre style={pre}>{JSON.stringify(payload, null, 2)}</pre>
            : <div style={subText}>The event body is no longer available (events are kept 90 days).</div>}
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Close</button>
          {(d.status === "failed" || d.status === "dead") && (
            <button className="btn btn-gold" disabled={busy} onClick={() => onRedeliver(d)}>{busy ? "Queuing…" : "Redeliver"}</button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Docs (static) ────────────────────────────────────────────────────────────────────────────────
function Docs({ baseUrl }) {
  const [openEvent, setOpenEvent] = useState(null);
  const [lang, setLang] = useState("node");
  const [openEp, setOpenEp] = useState(null);
  const h = (t) => <div style={{ fontSize: 15, fontWeight: 600, letterSpacing: "-.01em", margin: "26px 0 8px" }}>{t}</div>;
  const steps = (list) => (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {list.map(([t, d], i) => (
        <div key={t} style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
          <span style={{ flexShrink: 0, width: 24, height: 24, borderRadius: "50%", background: "var(--accent-weak)", color: "var(--accent)", fontWeight: 700, fontSize: 12, display: "flex", alignItems: "center", justifyContent: "center" }}>{i + 1}</span>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 13.5, fontWeight: 600 }}>{t}</div>
            <div style={subText}>{d.replace("<API base>", baseUrl)}</div>
          </div>
        </div>
      ))}
    </div>
  );
  return (
    <div>
      <p style={{ ...subText, margin: 0 }}>
        Every event is POSTed as JSON to each endpoint subscribed to it, signed with that endpoint's secret. Reply with any 2xx within 10 seconds; anything else is retried (1 m, 5 m, 30 m, 2 h, 8 h, 24 h) and then marked dead. The inbound API uses the same objects, so what you receive and what you fetch always match. API version <span style={code}>{API_VERSION}</span>.
      </p>

      {h("Connect to GoHighLevel")}
      {steps(GHL_CONNECT_STEPS)}
      <div className="alert alert-info" style={{ marginTop: 12 }}>{GHL_NOTE}</div>

      {h("Call the CRM from GoHighLevel")}
      <p style={{ ...subText, margin: "0 0 10px" }}>The reverse direction: a GHL workflow (or anything else) can read your clients and route, or trigger the same job actions a tech's tap does — on-the-way texts still go out from the CRM's number.</p>
      {steps(GHL_REVERSE_STEPS)}

      {h("Events & example payloads")}
      <div style={{ border: "1px solid var(--border)", borderRadius: "var(--r, 12px)", overflow: "hidden" }}>
        {EVENT_CATALOG.map((e) => (
          <div key={e.event} style={{ borderBottom: "1px solid var(--border)" }}>
            <button type="button" onClick={() => setOpenEvent(openEvent === e.event ? null : e.event)}
                    style={{ width: "100%", textAlign: "left", background: openEvent === e.event ? "var(--accent-weak)" : "transparent", border: "none", padding: "10px 14px", cursor: "pointer", font: "inherit", display: "flex", gap: 10, alignItems: "center" }}>
              <span style={{ ...code, flexShrink: 0 }}>{e.event}</span>
              <span style={{ fontSize: 13, flex: 1, minWidth: 0 }}>{e.desc}</span>
              <span style={{ color: "var(--text3)", fontSize: 12 }}>{openEvent === e.event ? "▲" : "▼"}</span>
            </button>
            {openEvent === e.event && (
              <div style={{ padding: "0 14px 12px" }}>
                <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 6 }}><CopyButton text={JSON.stringify(exampleEnvelope(e.event), null, 2)} label="Copy JSON" className="btn btn-ghost btn-xs" /></div>
                <pre style={pre}>{JSON.stringify(exampleEnvelope(e.event), null, 2)}</pre>
              </div>
            )}
          </div>
        ))}
      </div>

      {h("Verify the signature")}
      <p style={{ ...subText, margin: "0 0 10px" }}>Compute HMAC-SHA256 with your endpoint secret over <span style={code}>{"<timestamp>.<raw body>"}</span> and compare it to <span style={code}>v1</span> in the signature header with a constant-time compare. Reject timestamps older than a few minutes. GoHighLevel's Inbound Webhook can't verify signatures — keep its URL private and treat it like a password.</p>
      <div className="table-wrap" style={{ marginBottom: 12 }}>
        <table>
          <thead><tr><th>Header</th><th>Meaning</th></tr></thead>
          <tbody>{SIGNATURE_HEADERS.map(([k, v]) => <tr key={k}><td style={{ whiteSpace: "nowrap" }}><span style={code}>{k}</span></td><td style={{ fontSize: 12.5, color: "var(--text2)" }}>{v}</td></tr>)}</tbody>
        </table>
      </div>
      <div className="seg" style={{ marginBottom: 10 }}>
        <button className={lang === "node" ? "on" : ""} onClick={() => setLang("node")}>Node</button>
        <button className={lang === "python" ? "on" : ""} onClick={() => setLang("python")}>Python</button>
      </div>
      <pre style={pre}>{lang === "node" ? NODE_VERIFY_SNIPPET : PYTHON_VERIFY_SNIPPET}</pre>

      {h("API endpoints")}
      <p style={{ ...subText, margin: "0 0 10px" }}>Base URL <span style={code}>{baseUrl}</span>. Send your key as <span style={code}>Authorization: Bearer sk_scoop_…</span> (or <span style={code}>x-api-key</span>). Ids you don't own return 404. Tap a row for a curl example.</p>
      <div style={{ border: "1px solid var(--border)", borderRadius: "var(--r, 12px)", overflow: "hidden" }}>
        {API_ENDPOINTS.map((ep) => {
          const k = ep.method + ep.path;
          return (
            <div key={k} style={{ borderBottom: "1px solid var(--border)" }}>
              <button type="button" onClick={() => setOpenEp(openEp === k ? null : k)}
                      style={{ width: "100%", textAlign: "left", background: openEp === k ? "var(--accent-weak)" : "transparent", border: "none", padding: "10px 14px", cursor: "pointer", font: "inherit", display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                <Pill tone={ep.method === "GET" ? "green" : "blue"}>{ep.method}</Pill>
                <span style={{ ...code, flexShrink: 0 }}>{ep.path}</span>
                <Pill tone="gray">{ep.scope}</Pill>
                <span style={{ fontSize: 12.5, color: "var(--text2)", flex: 1, minWidth: 180 }}>{ep.desc}</span>
              </button>
              {openEp === k && <div style={{ padding: "0 14px 12px" }}><pre style={pre}>{curlExample(baseUrl, ep)}</pre></div>}
            </div>
          );
        })}
      </div>
      <div className="table-wrap" style={{ marginTop: 12 }}>
        <table>
          <thead><tr><th>Status</th><th>code</th><th>When</th></tr></thead>
          <tbody>{API_ERRORS.map(([s, c, d]) => <tr key={c}><td>{s}</td><td><span style={code}>{c}</span></td><td style={{ fontSize: 12.5, color: "var(--text2)" }}>{d}</td></tr>)}</tbody>
        </table>
      </div>
    </div>
  );
}

// ── The page ─────────────────────────────────────────────────────────────────────────────────────
export function ApiWebhooksSettings({ org, showToast }) {
  const isMobile = useIsMobile();
  const baseUrl = apiBaseUrl(SUPABASE_URL);
  const toast = (msg, kind) => (showToast ? showToast(msg, kind) : console.log(msg));

  const [keys, setKeys] = useState([]);
  const [endpoints, setEndpoints] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);            // id / action currently in flight (disables that control)
  const [filterEp, setFilterEp] = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  const [showCreateKey, setShowCreateKey] = useState(false);
  const [showAddEp, setShowAddEp] = useState(false);
  const [editEp, setEditEp] = useState(null);
  const [secret, setSecret] = useState(null);        // { title, intro, value }
  const [testResults, setTestResults] = useState({}); // endpoint_id → {ok,status_code,latency_ms,response_excerpt}
  const [selected, setSelected] = useState(null);    // delivery row open in the drawer
  const [payload, setPayload] = useState(null);
  const [payloadLoading, setPayloadLoading] = useState(false);
  const [docsOpen, setDocsOpen] = useState(false);

  const loadKeys = useCallback(async () => {
    const r = await sb.fn("manage-api-keys", { action: "list" }).catch(() => null);
    const list = Array.isArray(r) ? r : Array.isArray(r?.keys) ? r.keys : [];
    setKeys(list);
  }, []);
  const loadEndpoints = useCallback(async () => {
    setEndpoints(await rest(`webhook_endpoints?select=${ENDPOINT_COLS}&order=created_at.desc`));
  }, []);
  const loadDeliveries = useCallback(async (ep = filterEp, st = filterStatus) => {
    const q = [`select=${DELIVERY_COLS}`, "order=created_at.desc", "limit=50"];
    if (ep) q.push(`endpoint_id=eq.${encodeURIComponent(ep)}`);
    if (st) q.push(`status=eq.${encodeURIComponent(st)}`);
    setDeliveries(await rest(`webhook_deliveries?${q.join("&")}`));
  }, [filterEp, filterStatus]);
  const loadLogs = useCallback(async () => {
    setLogs(await rest(`api_request_log?select=${LOG_COLS}&order=created_at.desc&limit=50`));
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      // deliveries are loaded by the filter effect below once `loading` flips (one fetch, not two)
      await Promise.all([loadKeys(), loadEndpoints(), loadLogs()]);
      if (alive) setLoading(false);
    })();
    return () => { alive = false; };
    // initial load only; the filters have their own effect below
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { if (!loading) loadDeliveries(); }, [filterEp, filterStatus, loading, loadDeliveries]);

  // Open a delivery → pull its event body (org_events is org-scoped by RLS; a purged event comes back empty).
  useEffect(() => {
    if (!selected) { setPayload(null); return; }
    let alive = true;
    setPayloadLoading(true);
    rest(`org_events?id=eq.${selected.event_id}&select=${EVENT_COLS}`).then((rows) => {
      if (!alive) return;
      const ev = rows[0];
      setPayload(ev ? { id: ev.id, event: ev.event, created_at: ev.created_at, api_version: API_VERSION, organization: { id: org?.id, name: org?.name, slug: org?.slug }, data: ev.payload } : null);
      setPayloadLoading(false);
    });
    return () => { alive = false; };
  }, [selected, org]);

  // ── key actions ────────────────────────────────────────────────────────────────────────────────
  async function createKey(form) {
    setBusy("create-key");
    try {
      const r = await sb.fn("manage-api-keys", { action: "create", ...form });
      if (r?.error) { toast(r.error, "error"); return; }
      // The plaintext comes back exactly once (spec §4); accept the documented field plus defensive aliases.
      const plain = [r?.plaintext, r?.api_key, r?.key_plaintext, typeof r?.key === "string" ? r.key : null].find((v) => typeof v === "string" && v.startsWith("sk_scoop_"));
      setShowCreateKey(false);
      if (plain) setSecret({ title: "Your new API key", intro: `Use it as "Authorization: Bearer <key>" against ${baseUrl}.`, value: plain });
      else toast("Key created, but no plaintext was returned — revoke it and try again.", "error");
      await loadKeys();
    } catch (e) { console.error(e); toast("Could not create the key", "error"); }
    finally { setBusy(null); }
  }
  async function revokeKey(k) {
    if (!window.confirm(`Revoke "${k.label || k.key_prefix}"? Anything using it stops working immediately.`)) return;
    setBusy(k.id);
    try {
      const r = await sb.fn("manage-api-keys", { action: "revoke", id: k.id });
      if (r?.error) toast(r.error, "error"); else toast("Key revoked");
      await loadKeys();
    } catch (e) { console.error(e); toast("Could not revoke the key", "error"); }
    finally { setBusy(null); }
  }

  // ── endpoint actions ───────────────────────────────────────────────────────────────────────────
  async function createEndpoint(form) {
    setBusy("create-ep");
    try {
      const r = await sb.fn("manage-webhooks", { action: "create", ...form });
      if (r?.error) { toast(r.error, "error"); return; }
      setShowAddEp(false);
      if (r?.secret) setSecret({ title: "Endpoint signing secret", intro: "Use it to verify X-ScoopCRM-Signature on every delivery (see Docs → Verify the signature).", value: r.secret });
      toast("Endpoint added — press Test to send a ping");
      await loadEndpoints();
    } catch (e) { console.error(e); toast("Could not add the endpoint", "error"); }
    finally { setBusy(null); }
  }
  async function updateEndpoint(id, patch, okMsg) {
    setBusy(id);
    try {
      const r = await sb.fn("manage-webhooks", { action: "update", id, ...patch });
      if (r?.error) { toast(r.error, "error"); return false; }
      if (okMsg) toast(okMsg);
      await loadEndpoints();
      return true;
    } catch (e) { console.error(e); toast("Could not update the endpoint", "error"); return false; }
    finally { setBusy(null); }
  }
  async function rotateSecret(ep) {
    if (!window.confirm(`Rotate the signing secret for ${hostOf(ep.url)}? The old secret stops validating immediately.`)) return;
    setBusy(ep.id);
    try {
      const r = await sb.fn("manage-webhooks", { action: "rotate_secret", id: ep.id });
      if (r?.error) { toast(r.error, "error"); return; }
      if (r?.secret) setSecret({ title: "New signing secret", intro: `For ${ep.url}. Update your receiver before the next event.`, value: r.secret });
    } catch (e) { console.error(e); toast("Could not rotate the secret", "error"); }
    finally { setBusy(null); }
  }
  async function deleteEndpoint(ep) {
    if (!window.confirm(`Remove ${ep.url}? Its delivery history goes with it.`)) return;
    setBusy(ep.id);
    try {
      const r = await sb.fn("manage-webhooks", { action: "delete", id: ep.id });
      if (r?.error) toast(r.error, "error"); else toast("Endpoint removed");
      setTestResults((t) => { const n = { ...t }; delete n[ep.id]; return n; });
      await Promise.all([loadEndpoints(), loadDeliveries()]);
    } catch (e) { console.error(e); toast("Could not remove the endpoint", "error"); }
    finally { setBusy(null); }
  }
  async function testEndpoint(ep) {
    setBusy(`test-${ep.id}`);
    try {
      const r = await sb.fn("manage-webhooks", { action: "test", id: ep.id });
      if (r?.error && r.ok === undefined) { toast(r.error, "error"); return; }
      setTestResults((t) => ({ ...t, [ep.id]: { ...r, at: Date.now() } }));
      toast(r?.ok ? `Ping delivered (${r.status_code} in ${r.latency_ms} ms)` : `Ping failed${r?.status_code ? ` (${r.status_code})` : ""}`, r?.ok ? undefined : "error");
      await Promise.all([loadEndpoints(), loadDeliveries()]);
    } catch (e) { console.error(e); toast("Could not run the test", "error"); }
    finally { setBusy(null); }
  }
  async function redeliver(d) {
    setBusy(`redeliver-${d.id}`);
    try {
      const r = await sb.fn("manage-webhooks", { action: "redeliver", delivery_id: d.id });
      if (r?.error) toast(r.error, "error"); else toast("Queued for redelivery");
      setSelected(null);
      await loadDeliveries();
    } catch (e) { console.error(e); toast("Could not queue the redelivery", "error"); }
    finally { setBusy(null); }
  }

  // ── derived ────────────────────────────────────────────────────────────────────────────────────
  const keyById = useMemo(() => Object.fromEntries(keys.map((k) => [k.id, k])), [keys]);
  const epById = useMemo(() => Object.fromEntries(endpoints.map((e) => [e.id, e])), [endpoints]);
  const activeKeys = keys.filter((k) => !k.revoked_at && !(k.expires_at && new Date(k.expires_at) < new Date())).length;
  const activeEps = endpoints.filter((e) => e.active).length;
  const delivered = deliveries.filter((d) => d.status === "delivered").length;
  const deliveryRate = deliveries.length ? Math.round((delivered / deliveries.length) * 100) : null;
  const callsOk = logs.filter((l) => l.status_code && l.status_code < 400).length;
  const keyStatus = (k) => (k.revoked_at ? ["gray", "Revoked"] : k.expires_at && new Date(k.expires_at) < new Date() ? ["amber", "Expired"] : ["green", "Active"]);

  const metric = (label, value, sub, tone) => (
    <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: "var(--r, 12px)", padding: "12px 14px", boxShadow: "var(--shadow-sm)" }}>
      <div style={{ fontSize: 12, color: "var(--text2)", marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 700, letterSpacing: "-.02em", color: tone || "var(--text)", lineHeight: 1.1 }}>{value}</div>
      {sub && <div style={{ fontSize: 11.5, color: "var(--text2)", marginTop: 4 }}>{sub}</div>}
    </div>
  );

  return (
    <div>
      {/* ── Overview ── */}
      <div className="card">
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(140px,1fr))", gap: 12 }}>
          {metric("API keys", loading ? "…" : activeKeys, `${keys.length - activeKeys} revoked`, "var(--accent)")}
          {metric("Webhook endpoints", loading ? "…" : activeEps, `${endpoints.length - activeEps} paused`)}
          {metric("Delivery success", loading ? "…" : deliveryRate == null ? "—" : `${deliveryRate}%`, `of the last ${deliveries.length} deliveries`, deliveryRate == null ? undefined : deliveryRate >= 95 ? "var(--ok)" : "#8A5A06")}
          {metric("API calls", loading ? "…" : logs.length, logs.length ? `${callsOk} succeeded (last ${logs.length})` : "none yet")}
        </div>
      </div>

      {/* ── 1. API keys ── */}
      <div className="card">
        <div className="section-header">
          <div>
            <div className="card-title" style={{ marginBottom: 2 }}>API keys</div>
            <div style={subText}>Let GoHighLevel or your own tools read your clients and route, and trigger job actions. Keys are shown once and can be revoked any time.</div>
          </div>
          <button className="btn btn-gold" onClick={() => setShowCreateKey(true)} disabled={activeKeys >= 10} title={activeKeys >= 10 ? "Maximum of 10 active keys" : ""}>+ Create key</button>
        </div>
        {keys.length === 0 ? (
          <div className="empty" style={{ padding: "26px 12px" }}>{loading ? "Loading…" : "No API keys yet."}<p>Create one to call the CRM from GoHighLevel or your own scripts.</p></div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead><tr><th>Label</th><th>Prefix</th><th>Scopes</th><th>Rate</th><th>Created</th><th>Last used</th><th>Status</th><th></th></tr></thead>
              <tbody>
                {keys.map((k) => {
                  const [tone, label] = keyStatus(k);
                  return (
                    <tr key={k.id} style={{ opacity: k.revoked_at ? 0.6 : 1 }}>
                      <td style={{ fontWeight: 500 }}>{k.label || <span style={{ color: "var(--text3)" }}>untitled</span>}</td>
                      <td><span style={code}>{k.key_prefix}…</span></td>
                      <td><span style={{ display: "inline-flex", gap: 4, flexWrap: "wrap" }}>{(k.scopes || []).map((s) => <Pill key={s} tone="blue">{s}</Pill>)}</span></td>
                      <td style={{ whiteSpace: "nowrap", color: "var(--text2)" }}>{k.rate_per_min}/min</td>
                      <td style={{ whiteSpace: "nowrap", color: "var(--text2)" }} title={whenTitle(k.created_at)}>{ago(k.created_at)}</td>
                      <td style={{ whiteSpace: "nowrap", color: "var(--text2)" }} title={whenTitle(k.last_used_at)}>{k.last_used_at ? ago(k.last_used_at) : "never"}</td>
                      <td><Pill tone={tone} title={k.expires_at ? `Expires ${whenTitle(k.expires_at)}` : ""}>{label}</Pill></td>
                      <td style={{ textAlign: "right" }}>{!k.revoked_at && <button className="btn btn-danger btn-xs" disabled={busy === k.id} onClick={() => revokeKey(k)}>Revoke</button>}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── 2. Webhooks ── */}
      <div className="card">
        <div className="section-header">
          <div>
            <div className="card-title" style={{ marginBottom: 2 }}>Webhooks</div>
            <div style={subText}>We POST a signed JSON event to each URL when something happens — point one at a GoHighLevel Inbound Webhook to run your own automations.</div>
          </div>
          <button className="btn btn-gold" onClick={() => setShowAddEp(true)}>+ Add endpoint</button>
        </div>
        {endpoints.length === 0 ? (
          <div className="empty" style={{ padding: "26px 12px" }}>{loading ? "Loading…" : "No endpoints yet."}<p>Add a URL, pick the events you care about, then press Test.</p></div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {endpoints.map((ep) => {
              const t = testResults[ep.id];
              const paused = !ep.active;
              // disabled_reason is only ever set by the dispatcher's auto-pause; a manual pause leaves it null.
              const autoPaused = paused && /^auto-paused/i.test(ep.disabled_reason || "");
              return (
                <div key={ep.id} style={{ border: "1px solid var(--border)", borderRadius: "var(--r, 12px)", padding: "14px 16px", background: paused ? "var(--bg)" : "var(--surface)" }}>
                  <div style={{ display: "flex", gap: 12, alignItems: "flex-start", flexWrap: isMobile ? "wrap" : "nowrap" }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                        <span style={{ fontWeight: 600, fontSize: 14 }}>{hostOf(ep.url)}</span>
                        {autoPaused ? <Pill tone="red" title={ep.disabled_reason}>Auto-paused</Pill> : paused ? <Pill tone="gray">Paused</Pill> : <Pill tone="green">Active</Pill>}
                        {ep.consecutive_failures > 0 && <Pill tone="amber" title="Consecutive failures">{ep.consecutive_failures} failing</Pill>}
                      </div>
                      <div style={{ ...subText, fontFamily: MONO, fontSize: 11.5, overflowWrap: "anywhere" }}>{pathOf(ep.url)}</div>
                      {ep.description && <div style={{ ...subText, marginTop: 2 }}>{ep.description}</div>}
                      <div style={{ marginTop: 8 }}><EventPills events={ep.events} /></div>
                      <div style={{ ...subText, marginTop: 8, display: "flex", gap: 14, flexWrap: "wrap" }}>
                        <span title={whenTitle(ep.last_success_at)}>Last success: <strong style={{ color: ep.last_success_at ? "var(--ok)" : "var(--text3)", fontWeight: 600 }}>{ep.last_success_at ? ago(ep.last_success_at) : "never"}</strong></span>
                        <span title={whenTitle(ep.last_failure_at)}>Last failure: <strong style={{ color: ep.last_failure_at ? "var(--danger)" : "var(--text3)", fontWeight: 600 }}>{ep.last_failure_at ? ago(ep.last_failure_at) : "never"}</strong></span>
                      </div>
                      {t && (
                        <div style={{ marginTop: 8, fontSize: 12.5, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                          <Pill tone={t.ok ? "green" : "red"}>{t.ok ? "Test passed" : "Test failed"}</Pill>
                          <span style={{ color: "var(--text2)" }}>{t.status_code ? `HTTP ${t.status_code}` : "no response"}{t.latency_ms != null ? ` · ${t.latency_ms} ms` : ""}</span>
                          {t.response_excerpt && <span style={{ fontFamily: MONO, fontSize: 11.5, color: "var(--text2)", overflowWrap: "anywhere" }}>{String(t.response_excerpt).slice(0, 160)}</span>}
                          {t.error && !t.response_excerpt && <span style={{ color: "var(--danger)" }}>{t.error}</span>}
                        </div>
                      )}
                    </div>
                    <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", flexShrink: 0, fontSize: 13, color: "var(--text)", fontWeight: 500 }}>
                      <input type="checkbox" checked={!!ep.active} disabled={busy === ep.id}
                             onChange={(e) => updateEndpoint(ep.id, { active: e.target.checked }, e.target.checked ? "Endpoint resumed" : "Endpoint paused")} />
                      {ep.active ? "On" : "Off"}
                    </label>
                  </div>
                  <div style={{ display: "flex", gap: 6, marginTop: 12, flexWrap: "wrap" }}>
                    <button className="btn btn-primary btn-xs" disabled={busy === `test-${ep.id}`} onClick={() => testEndpoint(ep)}>{busy === `test-${ep.id}` ? "Pinging…" : "Test"}</button>
                    <button className="btn btn-ghost btn-xs" disabled={busy === ep.id} onClick={() => setEditEp(ep)}>Edit</button>
                    <button className="btn btn-ghost btn-xs" disabled={busy === ep.id} onClick={() => rotateSecret(ep)}>Rotate secret</button>
                    <button className="btn btn-danger btn-xs" disabled={busy === ep.id} onClick={() => deleteEndpoint(ep)} style={{ marginLeft: "auto" }}>Remove</button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── 3. Recent deliveries ── */}
      <div className="card">
        <div className="section-header" style={{ flexWrap: "wrap", gap: 10 }}>
          <div>
            <div className="card-title" style={{ marginBottom: 2 }}>Recent deliveries</div>
            <div style={subText}>The last 50 webhook attempts. Click one to see exactly what was sent.</div>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <select value={filterEp} onChange={(e) => setFilterEp(e.target.value)} style={{ padding: "7px 10px", fontSize: 12.5 }}>
              <option value="">All endpoints</option>
              {endpoints.map((ep) => <option key={ep.id} value={ep.id}>{hostOf(ep.url)}{ep.description ? ` — ${ep.description}` : ""}</option>)}
            </select>
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} style={{ padding: "7px 10px", fontSize: 12.5 }}>
              <option value="">Any status</option>
              {["pending", "delivered", "failed", "dead"].map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <button className="btn btn-ghost btn-sm" onClick={() => loadDeliveries()}>Refresh</button>
          </div>
        </div>
        {deliveries.length === 0 ? (
          <div className="empty" style={{ padding: "26px 12px" }}>{loading ? "Loading…" : "Nothing delivered yet."}<p>Deliveries appear here as events fire — press Test on an endpoint to see one.</p></div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead><tr><th>Event</th><th>Endpoint</th><th>Status</th><th>Attempts</th><th>Last code</th><th>When</th><th></th></tr></thead>
              <tbody>
                {deliveries.map((d) => {
                  const ep = epById[d.endpoint_id];
                  return (
                    <tr key={d.id} onClick={() => setSelected(d)} style={{ cursor: "pointer" }}>
                      <td><div style={{ fontWeight: 500 }}>{eventLabel(d.event)}</div><div style={{ fontSize: 11.5, color: "var(--text2)", fontFamily: MONO }}>{d.event}</div></td>
                      <td style={{ color: "var(--text2)", maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={ep?.url || d.endpoint_id}>{ep ? hostOf(ep.url) : <span style={{ color: "var(--text3)" }}>removed</span>}</td>
                      <td><Pill tone={DELIVERY_TONE[d.status] || "gray"}>{d.status}</Pill></td>
                      <td style={{ color: "var(--text2)" }}>{d.attempts}</td>
                      <td>{d.last_status_code ? <Pill tone={codeTone(d.last_status_code)}>{d.last_status_code}</Pill> : <span style={{ color: "var(--text3)" }}>—</span>}</td>
                      <td style={{ whiteSpace: "nowrap", color: "var(--text2)" }} title={whenTitle(d.created_at)}>{ago(d.created_at)}</td>
                      <td style={{ textAlign: "right" }}>
                        {(d.status === "failed" || d.status === "dead") && (
                          <button className="btn btn-ghost btn-xs" disabled={busy === `redeliver-${d.id}`} onClick={(e) => { e.stopPropagation(); redeliver(d); }}>Redeliver</button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── 4. Recent API calls ── */}
      <div className="card">
        <div className="section-header">
          <div>
            <div className="card-title" style={{ marginBottom: 2 }}>Recent API calls</div>
            <div style={subText}>The last 50 requests made with your API keys.</div>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={loadLogs}>Refresh</button>
        </div>
        {logs.length === 0 ? (
          <div className="empty" style={{ padding: "26px 12px" }}>{loading ? "Loading…" : "No API calls yet."}<p>Requests show up here the moment a key is used.</p></div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead><tr><th>Method</th><th>Path</th><th>Status</th><th>Key</th><th>Time</th><th>When</th></tr></thead>
              <tbody>
                {logs.map((l) => {
                  const k = keyById[l.api_key_id];
                  return (
                    <tr key={l.id}>
                      <td><Pill tone={l.method === "GET" ? "green" : "blue"}>{l.method || "?"}</Pill></td>
                      <td><span style={{ ...code, overflowWrap: "anywhere" }}>{l.path}</span></td>
                      <td>{l.status_code ? <Pill tone={codeTone(l.status_code)}>{l.status_code}</Pill> : <span style={{ color: "var(--text3)" }}>—</span>}</td>
                      <td style={{ whiteSpace: "nowrap" }}>{k ? <span title={k.label || ""} style={code}>{k.key_prefix}…</span> : <span style={{ color: "var(--text3)" }}>{l.api_key_id ? "removed" : "none"}</span>}</td>
                      <td style={{ whiteSpace: "nowrap", color: "var(--text2)" }}>{l.duration_ms != null ? `${l.duration_ms} ms` : "—"}</td>
                      <td style={{ whiteSpace: "nowrap", color: "var(--text2)" }} title={whenTitle(l.created_at)}>{ago(l.created_at)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── 5. Docs ── */}
      <div className="card">
        <div className="section-header" style={{ marginBottom: docsOpen ? 18 : 0 }}>
          <div>
            <div className="card-title" style={{ marginBottom: 2 }}>Docs</div>
            <div style={subText}>Event payloads, signature verification, API endpoints, and the GoHighLevel how-tos.</div>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={() => setDocsOpen((o) => !o)}>{docsOpen ? "Hide docs" : "Show docs"}</button>
        </div>
        {docsOpen && <Docs baseUrl={baseUrl} />}
      </div>

      {/* ── modals ── */}
      {showCreateKey && <CreateKeyModal busy={busy === "create-key"} onSave={createKey} onClose={() => setShowCreateKey(false)} />}
      {showAddEp && <EndpointModal busy={busy === "create-ep"} onSave={createEndpoint} onClose={() => setShowAddEp(false)} />}
      {editEp && (
        <EndpointModal key={editEp.id} initial={editEp} busy={busy === editEp.id}
                       onSave={async (form) => { if (await updateEndpoint(editEp.id, form, "Endpoint updated")) setEditEp(null); }}
                       onClose={() => setEditEp(null)} />
      )}
      {selected && (
        <DeliveryDrawer delivery={selected} endpoint={epById[selected.endpoint_id]} payload={payload} loading={payloadLoading}
                        busy={busy === `redeliver-${selected.id}`} onRedeliver={redeliver} onClose={() => setSelected(null)} />
      )}
      {secret && <SecretModal title={secret.title} intro={secret.intro} value={secret.value} onClose={() => setSecret(null)} />}
    </div>
  );
}
